import type { z } from "zod";
import { AsyncQueue } from "@/shared/async-queue";
import type { AgentToolRequest, Message } from "@/shared/messages";
import { newId } from "@/shared/prefixed-id";
import type { LLMToolDefinition } from "./resources";

// LLMBase.generateMessage()
export type LLMGenerateMessageChunk =
  | { type: "content"; content: string }
  | { type: "tools"; tools: AgentToolRequest[] }
  | { type: "end" }
  | { type: "error"; error: string };

export interface LLMGenerateMessageJob {
  id: string;
  cancel: () => void;
  stream: AsyncQueue<LLMGenerateMessageChunk>;
  _abortController: AbortController;
}

// LLMBase.generateObject()
export type LLMGenerateObjectChunk<T extends z.ZodObject> =
  | { type: "content"; data: z.infer<T> }
  | { type: "error"; error: string };

/**
 * Base class for all LLMs providers.
 */
export abstract class LLMBase<ConfigSchema extends z.ZodObject> {
  config: z.infer<ConfigSchema>;

  constructor(configSchema: ConfigSchema, config: Partial<z.infer<ConfigSchema>>) {
    this.config = configSchema.parse({ ...config });
  }

  protected createGenerateMessageJob(): LLMGenerateMessageJob {
    const id = newId("job");
    const stream = new AsyncQueue<LLMGenerateMessageChunk>();
    const _abortController = new AbortController();
    const job: LLMGenerateMessageJob = {
      id,
      stream,
      cancel: () => _abortController.abort(),
      _abortController,
    };
    return job;
  }

  // To be impemented by subclasses
  abstract generateMessage(params: {
    messages: Message[];
    tools: LLMToolDefinition[];
  }): Promise<LLMGenerateMessageJob>;

  abstract generateObject<T extends z.ZodObject>(params: {
    messages: Message[];
    schema: T;
  }): Promise<LLMGenerateObjectChunk<T>>;
}
